import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { useMedia, useMount } from 'react-use';

import { useGeneratePath } from '../../utils/hooks/useRouterPath';
import { useSocketRoom } from '../../utils/hooks/useSocketRoom';
import { useStaticUrls } from '../../utils/hooks/useStaticUrls';
import { getFromLocalStorage, setToLocalStorage } from '../../utils/storage';
import { UI_KIT_HEADER_TYPOGRAPHY } from '../constants';
import { H1 } from '../header-text/header-text';
import { QuizCreateCard } from '../quiz-create-card/quiz-create-card';
import { QuizList } from '../quiz-list/quiz-list';
import { getIsTeacher } from '../user/selectors';

import { MEDIA_QUERY_DESKTOP_L } from '../../utils/von-dyck-media-queries';
import { QuizChildrenCountCircle } from './__children-count-circle/quiz__children-count-circle';
import { QuizTimerCircle } from './__timer-circle/quiz__timer-circle';
import { QuizFinisher } from './__finisher/quiz__finisher';
import QuizHeader from './__header/quiz__header';
import { QuizOption } from './__option/quiz__option';
import { QuizQuestionStatistics } from './__question-statistics/quiz__question-statistics';
import QuizStartScreen from './__start-screen/quiz__start-screen';
import { studentRoomsNames, TEACHER_QUIZ_MODE } from './constants';
import { getBunkerQuizLists, getQuizToPlay, getQuizzes, getRoomPin, getStudentsMap } from './selectors';
import { quizActions } from './slice';
import {
    Question,
    Quiz as QuizType,
    AnswerStatus,
    Student,
    StudentRooms,
    StatItem,
    QuestionResults,
    CARD_COLORS,
} from './typings';

import './quiz.scss';

interface QuizPageParams {
    quizId?: string;
}

const TeacherStudentSocketRoom = ({
    pin,
    student,
    studentRoomsRecordRef,
}: {
    pin: string | null;
    student: Student;
    studentRoomsRecordRef: React.MutableRefObject<StudentRooms>;
}) => {
    const roomStudent = `quiz:${pin}:${student.id}`;
    const { emit } = useSocketRoom(roomStudent, {
        connect: () => emit('initial.name', student.name),
    });

    useEffect(() => {
        const studentRoomsRecord = studentRoomsRecordRef.current;
        studentRoomsRecord[student.id] = emit;
        return () => void delete studentRoomsRecord[student.id];
    }, [emit, student.id, studentRoomsRecordRef]);

    return null;
};

const NAMES = [...studentRoomsNames];

// eslint-disable-next-line react/no-multi-comp
export const Quiz = () => {
    const dispatch = useDispatch();

    const history = useHistory();
    const staticUrls = useStaticUrls();
    const generatePath = useGeneratePath();
    const isBigScreen = useMedia(MEDIA_QUERY_DESKTOP_L);

    const { quizId } = useParams<QuizPageParams>();

    const intl = useIntl();

    const roomPin = useSelector(getRoomPin);

    const [pin] = useState(() => {
        // генерируется пин и сохраняется в урл
        // использовать teacher id?
        if (typeof window !== 'undefined') {
            const p = roomPin || window.location.hash.slice(1) || Math.random().toString().slice(-6);
            if (!roomPin) {
                dispatch(quizActions.setRoomPin(p));
            }

            history.replace(`#${p}`);
            return p;
        }

        return null;
    });

    const quizToPlay = useSelector(getQuizToPlay);

    const [quizGameName, setQuizGameName] = useState(quizToPlay?.title || '');
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number | null>(null);
    const [questions, setQuestions] = useState<Question[]>(quizToPlay?.questions || []);

    const userQuizzes = useSelector(getQuizzes);
    const bunkerQuizLists = useSelector(getBunkerQuizLists);

    const [mode, setMode] = useState(TEACHER_QUIZ_MODE.START_SCREEN);

    const studentsMap = useSelector(getStudentsMap);
    const isTeacher = useSelector(getIsTeacher);
    const allStudentsCount = Object.values(studentsMap).length;
    const onlineStudentsCount = Object.values(studentsMap).filter(({ isOnline }) => isOnline).length;

    const curAnswersCount = Object.values(studentsMap).filter(
        ({ answers, isOnline }) => isOnline && currentQuestionIndex !== null && answers?.[currentQuestionIndex]
    ).length;

    useEffect(() => {
        if (isTeacher) {
            dispatch(quizActions.getQuizzes());
        }
    }, [dispatch, isTeacher]);

    const getStudentName = () => {
        return NAMES[allStudentsCount % NAMES.length];
    };

    const studentRoomsRecordRef = useRef<StudentRooms>({});

    useMount(() => {
        if (quizId !== undefined) {
            for (const subject of bunkerQuizLists) {
                const targetQuiz = subject.quizzes.find((quiz) => quiz.id === quizId);

                if (targetQuiz) {
                    history.replace(staticUrls.labPreviewQuiz);
                    dispatch(quizActions.setQuizToPreview(targetQuiz));
                    break;
                }
            }
        }
    });

    const save = (questionIndex: number, answerIndex: number, answerStatus: AnswerStatus, studentId: string) => {
        if (questionIndex === null) {
            return;
        }

        const dataFromLocalStorage = JSON.parse(getFromLocalStorage('quiz', {})) || {};
        const currentRoom = dataFromLocalStorage[String(pin)] || [];

        currentRoom.push({ questionIndex, answerIndex, answerStatus, studentId });

        setToLocalStorage(
            'quiz',
            JSON.stringify({
                [String(pin)]: currentRoom,
            })
        );

        const studentRoomEmit = studentRoomsRecordRef.current[studentId];

        studentRoomEmit?.('show.answerStatus', answerStatus);
    };

    const [renderKey, setRenderKey] = useState(0);

    const room = `quiz:${pin}`;
    const { emit } = useSocketRoom(room, {
        'student.join': ({ id }: { id: string }) => {
            const student = studentsMap[id];

            if (!student) {
                const name = getStudentName();
                dispatch(quizActions.addStudent({ id, name }));
            } else {
                dispatch(quizActions.setStudentOnlineStatus({ id, isOnline: true }));

                const studentRoomEmit = studentRoomsRecordRef.current[id];
                studentRoomEmit?.('initial.name', student.name);
            }

            const studentRoomEmit = studentRoomsRecordRef.current[id];

            if (mode === TEACHER_QUIZ_MODE.FINAL_SCREEN) {
                emit('show.results');
            } else if (questions && currentQuestionIndex !== null) {
                if (student.answers?.[currentQuestionIndex]) {
                    studentRoomEmit?.('show.answerStatus', student.answers?.[currentQuestionIndex]);
                } else {
                    studentRoomEmit?.('question', {
                        question: questions[currentQuestionIndex],
                        questionIndex: currentQuestionIndex,
                        questionsCount: questions.length,
                    });
                }
            }
            setRenderKey(Date.now());
        },
        'student.left': ({ id }: { id: string }) => {
            const student = studentsMap[id];

            if (student) {
                dispatch(quizActions.setStudentOnlineStatus({ id, isOnline: false }));
            }
        },
        'student.answer': ({ answerIndex, questionIndex, studentId }) => {
            const student = studentsMap[studentId];

            if (student && currentQuestionIndex !== null) {
                const answerStatus = questions[questionIndex].answers[answerIndex].correct ? 'correct' : 'incorrect';

                dispatch(
                    quizActions.setStudentAnswerStatus({
                        id: studentId,
                        questionIndex: currentQuestionIndex,
                        answerStatus,
                    })
                );

                save(questionIndex, answerIndex, answerStatus, studentId);
            }
        },
    });

    const handleBeforeUnload = useCallback(() => {
        setToLocalStorage(
            'quiz',
            JSON.stringify({
                [String(pin)]: null,
            })
        );
    }, [pin]);

    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);

        return function () {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [handleBeforeUnload]);

    const showResults = () => {
        emit('show.results');
    };

    const showQuestionResult = (index: number) => {
        const allStats = JSON.parse(getFromLocalStorage('quiz', {})) || {};

        if (!pin) {
            return;
        }

        const currentRoomStats = allStats[pin] || [];

        const answers: StatItem[] = currentRoomStats.filter((statItem: StatItem) => statItem.questionIndex === index);

        if (currentQuestionIndex !== null) {
            const rightAnswerIndex = questions[currentQuestionIndex].answers.findIndex((answer) => answer.correct);

            const results = questions[currentQuestionIndex].answers.reduce<QuestionResults>(
                (resultsObj, answer, answerIndex) => {
                    resultsObj[answerIndex] = 0;
                    return resultsObj;
                },
                {}
            );

            questions[currentQuestionIndex].answers.forEach((answer, i) => {
                results[i] = 0;
            });

            for (const answer of answers) {
                results[answer.answerIndex] = results[answer.answerIndex] + 1;
            }

            const data = {
                rightAnswerIndex,
                results,
            };

            dispatch(quizActions.setStatData(data));
        }
    };

    useEffect(() => {
        if (currentQuestionIndex !== null && questions) {
            emit('question', {
                question: questions[currentQuestionIndex],
                questionIndex: currentQuestionIndex,
                questionsCount: questions.length,
            });
        }
    }, [emit, currentQuestionIndex, questions]);

    const questionsLastIndex = questions && questions.length - 1;
    const isLastQuestion = currentQuestionIndex === questionsLastIndex;

    // рестарт сначала с сохранением pin
    const restartQuiz = () => {
        emit('restart.quiz');

        setMode(TEACHER_QUIZ_MODE.START_SCREEN);
        setCurrentQuestionIndex(null);
        setQuizGameName('');

        dispatch(quizActions.resetAllStudentsAnswersStatus());

        setToLocalStorage(
            'quiz',
            JSON.stringify({
                [String(pin)]: null,
            })
        );

        emit('quiz:reset');
    };

    const handleRestartQuiz = () => {
        restartQuiz();
    };

    const handleHeaderReturnButtonClick = () => {
        restartQuiz();
    };

    let handleMainButtonClick, handleStatButtonClick;

    if (mode === TEACHER_QUIZ_MODE.START_SCREEN) {
        handleMainButtonClick = () => {
            setMode(TEACHER_QUIZ_MODE.QUESTION);
            setCurrentQuestionIndex(0);
        };
    } else if (currentQuestionIndex !== null) {
        handleStatButtonClick = () => {
            setMode(TEACHER_QUIZ_MODE.STATISTICS);
            showQuestionResult(currentQuestionIndex);
            emit('quiz:showCorrectAnswer');
        };

        if (!isLastQuestion) {
            handleMainButtonClick = () => {
                setMode(TEACHER_QUIZ_MODE.QUESTION);
                setCurrentQuestionIndex(currentQuestionIndex + 1);
            };
        } else {
            handleMainButtonClick = () => {
                setMode(TEACHER_QUIZ_MODE.FINAL_SCREEN);
                showResults();
            };
        }
    }

    const handleStartGame = ({ title: quizName, questions: quizQuestions }: QuizType) => {
        setQuizGameName(quizName);
        setQuestions(quizQuestions);
    };

    const handlePreviewCustomQuiz = (quiz: QuizType) => {
        dispatch(quizActions.setQuizToPreview(quiz));
        const path = generatePath(staticUrls.labQuizEditPreview, { uuid: quiz.id });
        history.push(path);
    };

    const handlePreviewBunkerQuiz = (quiz: QuizType) => {
        dispatch(quizActions.setQuizToPreview(quiz));

        history.push(staticUrls.labPreviewQuiz);
    };

    const handleQuizCreateCardClick = () => {
        history.push(staticUrls.labCreateQuiz);
    };

    const handleQuizEditClick = (quiz: QuizType) => {
        if (!quiz.id) {
            return;
        }

        dispatch(quizActions.setQuizToPreview(quiz));

        history.push(
            generatePath(staticUrls.labEditQuiz, {
                uuid: quiz.id,
            })
        );
    };

    const handleQuizDeleteClick = ({ id }: QuizType) => {
        if (!id) {
            return;
        }

        dispatch(quizActions.deleteQuiz(id));
    };

    return (
        <div className="quiz-teacher">
            {mode === TEACHER_QUIZ_MODE.FINAL_SCREEN && (
                <>
                    {isBigScreen && <div className="quiz-teacher__background-cover-left" />}
                    {isBigScreen && <div className="quiz-teacher__background-cover-right" />}
                </>
            )}
            <QuizHeader
                handleMainButtonClick={handleMainButtonClick}
                handleStatButtonClick={handleStatButtonClick}
                isGameSelected={Boolean(quizGameName)}
                isLastQuestion={isLastQuestion}
                isStatButtonDisabled={!curAnswersCount}
                mode={mode}
                onReturnButtonClick={handleHeaderReturnButtonClick}
                onlineStudentsCount={onlineStudentsCount}
                questionIndex={currentQuestionIndex}
                totalQuestions={questions && questions.length}
            />
            {Object.values(studentsMap).map((student) => (
                <TeacherStudentSocketRoom
                    key={`${student.id}-${renderKey}`}
                    pin={pin}
                    student={student}
                    studentRoomsRecordRef={studentRoomsRecordRef}
                />
            ))}
            {mode === TEACHER_QUIZ_MODE.START_SCREEN && (
                <div className="quiz-teacher__connect">
                    {!quizGameName && (
                        <>
                            {isTeacher && (
                                <>
                                    <QuizList
                                        beforeCards={<QuizCreateCard onClick={handleQuizCreateCardClick} />}
                                        isCustom
                                        onQuizDeleteClick={handleQuizDeleteClick}
                                        onQuizEditClick={handleQuizEditClick}
                                        onQuizPreviewClick={handlePreviewCustomQuiz}
                                        onStartGame={handleStartGame}
                                        quizzes={userQuizzes}
                                        title={intl.formatMessage({ id: 'quiz.list.customQuizListTitle' })}
                                    />
                                    <div className="quiz-teacher__games-divider" />
                                </>
                            )}
                            {bunkerQuizLists.map(({ title, quizzes, cardImgSrcSet }) => (
                                <QuizList
                                    cardImgSrcSet={cardImgSrcSet}
                                    key={title}
                                    onQuizPreviewClick={handlePreviewBunkerQuiz}
                                    onStartGame={handleStartGame}
                                    quizzes={quizzes}
                                    title={title}
                                />
                            ))}
                        </>
                    )}
                    {quizGameName && (
                        <QuizStartScreen
                            handleMainButtonClick={handleMainButtonClick}
                            onlineStudentsCount={onlineStudentsCount}
                            pin={pin}
                        />
                    )}
                </div>
            )}
            {mode === TEACHER_QUIZ_MODE.STATISTICS && currentQuestionIndex !== null && (
                <QuizQuestionStatistics
                    answers={questions[currentQuestionIndex].answers}
                    title={questions[currentQuestionIndex].title}
                />
            )}
            {mode === TEACHER_QUIZ_MODE.QUESTION && currentQuestionIndex !== null && (
                <div className="quiz-teacher__questions-wrapper">
                    <div className="quiz-teacher__main-content-wrapper">
                        <div className="quiz-teacher__question">
                            <QuizTimerCircle onTimerEnd={handleStatButtonClick} />
                            <H1 mix="quiz-teacher__quiz-title" typography={UI_KIT_HEADER_TYPOGRAPHY.headlineLMedium}>
                                {questions[currentQuestionIndex].title}
                            </H1>
                            <QuizChildrenCountCircle
                                curAnswersCount={curAnswersCount}
                                onlineStudentsCount={onlineStudentsCount}
                            />
                        </div>
                        <div className="quiz-teacher__options">
                            {questions[currentQuestionIndex].answers.map((answer, answerIndex) => (
                                <QuizOption color={CARD_COLORS[answerIndex]} key={answerIndex} option={answer.value} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {mode === TEACHER_QUIZ_MODE.FINAL_SCREEN && <QuizFinisher handleRestartQuiz={handleRestartQuiz} />}
        </div>
    );
};
